import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Observable, of } from 'rxjs';
import { catchError, map, take, tap } from 'rxjs/operators';
import { AddressModel, CarrierModel, CustomHttpErrorResponse, Error, ListObject } from '../../../../../../../common';
import { NotificationService } from '../../../../../../../common/services';
import { NavigateUserService } from '../../../../../../services/navigate-user.service';
import { DockBrief } from '../../../../dock';
import { CompartmentRuleItem, OverlappingRuleErrorModel } from '../../../models/compartment-rule-item.model';
import { CompartmentRuleListFacade } from '../list/compartment-rule-list.facade';
import {
  CompartmentRuleItemCreate,
  CompartmentRuleItemDelete,
  CompartmentRuleItemGetCarriers,
  CompartmentRuleItemGetDeliveryAddresses,
  CompartmentRuleItemGetDocks,
  CompartmentRuleItemGetPickupAddresses,
  CompartmentRuleItemInitialState,
  CompartmentRuleItemLoad,
  CompartmentRuleItemsDelete,
} from './compartment-rule-item.actions';
import { CompartmentRuleItemService } from './compartment-rule-item.service';

export interface CompartmentRuleItemStateModel {
  compartmentRuleItem: CompartmentRuleItem;
  docks: DockBrief[];
  carriers: CarrierModel[];
  pickupAddresses: AddressModel[];
  canLoadMorePickupAddresses: boolean;
  deliveryAddresses: AddressModel[];
  canLoadMoredeliveryAddresses: boolean;
  loaded: boolean;
  overlappingRuleError: OverlappingRuleErrorModel;
}

const initialState: CompartmentRuleItemStateModel = {
  compartmentRuleItem: null,
  docks: [],
  carriers: [],
  pickupAddresses: [],
  canLoadMorePickupAddresses: true,
  deliveryAddresses: [],
  canLoadMoredeliveryAddresses: true,
  loaded: false,
  overlappingRuleError: null,
};

@State<CompartmentRuleItemStateModel>({
  name: 'compartmentRuleItem',
  defaults: initialState,
})
@Injectable()
export class CompartmentRuleItemState {
  constructor(
    private compartmentRuleItemService: CompartmentRuleItemService,
    private compartmentRuleListFacade: CompartmentRuleListFacade,
    private notificationService: NotificationService,
    private navigateUserService: NavigateUserService,
  ) {}

  @Selector()
  static getCompartmentRuleItem(state: CompartmentRuleItemStateModel): CompartmentRuleItem {
    return state.compartmentRuleItem;
  }

  @Selector()
  static getCompartmentRuleItemName(state: CompartmentRuleItemStateModel): string {
    return state.compartmentRuleItem?.name;
  }

  @Selector()
  static getCompartmentRuleItemId(state: CompartmentRuleItemStateModel): number {
    return state.compartmentRuleItem?.id;
  }

  @Selector()
  static getDocks(state: CompartmentRuleItemStateModel): DockBrief[] {
    return state.docks;
  }

  @Selector()
  static getCarriers(state: CompartmentRuleItemStateModel): CarrierModel[] {
    return state.carriers;
  }

  @Selector()
  static getPickupAddresses(state: CompartmentRuleItemStateModel): AddressModel[] {
    return state.pickupAddresses;
  }

  @Selector()
  static isMorePickupAddressesLoadable(state: CompartmentRuleItemStateModel): boolean {
    return state.canLoadMorePickupAddresses;
  }

  @Selector()
  static getDeliveryAddresses(state: CompartmentRuleItemStateModel): AddressModel[] {
    return state.deliveryAddresses;
  }

  @Selector()
  static isMoreDeliveryAddressesLoadable(state: CompartmentRuleItemStateModel): boolean {
    return state.canLoadMoredeliveryAddresses;
  }

  @Selector()
  static getLoaded(state: CompartmentRuleItemStateModel): boolean {
    return state.loaded;
  }

  @Selector()
  static getOverlappingRuleError(state: CompartmentRuleItemStateModel): OverlappingRuleErrorModel {
    return state.overlappingRuleError;
  }

  @Action(CompartmentRuleItemLoad)
  getCompartmentRuleItem(
    ctx: StateContext<CompartmentRuleItemStateModel>,
    { payload }: CompartmentRuleItemLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.compartmentRuleItemService.getCompartmentRuleItem(payload).pipe(
      take(1),
      map((compartmentRuleItem: CompartmentRuleItem) => {
        ctx.patchState({ compartmentRuleItem, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(CompartmentRuleItemGetDocks)
  getDocks(
    ctx: StateContext<CompartmentRuleItemStateModel>,
    { payload }: CompartmentRuleItemGetDocks,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.compartmentRuleItemService.getDocks(payload).pipe(
      take(1),
      map((data: ListObject<DockBrief>) => {
        ctx.patchState({ docks: data.items, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(CompartmentRuleItemGetCarriers)
  getCarriers(
    ctx: StateContext<CompartmentRuleItemStateModel>,
    { payload }: CompartmentRuleItemGetCarriers,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.compartmentRuleItemService.getCarriers(payload).pipe(
      take(1),
      map((carriers: CarrierModel[]) => {
        ctx.patchState({ carriers, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(CompartmentRuleItemGetPickupAddresses)
  getPickupAddresses(
    ctx: StateContext<CompartmentRuleItemStateModel>,
    { payload }: CompartmentRuleItemGetPickupAddresses,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.compartmentRuleItemService.getAddresses(payload).pipe(
      take(1),
      map((pickupAddresses: AddressModel[]) => {
        ctx.patchState({
          pickupAddresses,
          canLoadMorePickupAddresses: pickupAddresses.length >= payload.limit,
          loaded: true,
        });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(CompartmentRuleItemGetDeliveryAddresses)
  getDeliveryAddresses(
    ctx: StateContext<CompartmentRuleItemStateModel>,
    { payload }: CompartmentRuleItemGetDeliveryAddresses,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.compartmentRuleItemService.getAddresses(payload).pipe(
      take(1),
      map((deliveryAddresses: AddressModel[]) => {
        ctx.patchState({
          deliveryAddresses,
          canLoadMoredeliveryAddresses: deliveryAddresses.length >= payload.limit,
          loaded: true,
        });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(CompartmentRuleItemCreate)
  createCompartmentRuleItem(
    ctx: StateContext<CompartmentRuleItemStateModel>,
    { payload }: CompartmentRuleItemCreate,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | CompartmentRuleItem> {
    return this.compartmentRuleItemService.createCompartmentRuleItem(payload).pipe(
      take(1),
      tap(() => {
        const message = payload.body.id
          ? 'api.compartment.rules.success.update'
          : 'api.compartment.rules.success.create';
        if (payload.dialog) {
          payload.dialog.close();
        }
        this.notificationService.success(message);
        this.navigateToRulesList(payload.hubId, payload.compartmentId);
      }),
      // TODO: write interface and add type to CustomHttpErrorResponse<interface>
      catchError(({ error }: HttpErrorResponse) => {
        if (error?.error?.slots) {
          ctx.patchState({ overlappingRuleError: error });
        } else {
          return of(this.notificationService.error(error.message));
        }
      }),
    );
  }

  @Action(CompartmentRuleItemsDelete)
  deleteCompartmentRuleItems(
    ctx: StateContext<CompartmentRuleItemStateModel>,
    { payload }: CompartmentRuleItemsDelete,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | CompartmentRuleItem> {
    return this.compartmentRuleItemService.deleteCompartmentRuleItems(payload).pipe(
      take(1),
      tap(() => {
        payload.selection.clear();
        this.compartmentRuleListFacade.loadAll();
        payload.dialog.close();
        this.notificationService.success('api.compartment.rules.success.delete');
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(CompartmentRuleItemDelete)
  deleteCompartmentRuleItem(
    ctx: StateContext<CompartmentRuleItemStateModel>,
    { payload }: CompartmentRuleItemDelete,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | CompartmentRuleItem> {
    return this.compartmentRuleItemService.deleteCompartmentRuleItems(payload).pipe(
      take(1),
      tap(() => {
        this.notificationService.success('api.compartment.rules.success.delete');
        payload.dialog.close();
        this.navigateToRulesList(payload.hubId, payload.compartmentId);
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(CompartmentRuleItemInitialState)
  setInitialState(ctx: StateContext<CompartmentRuleItemStateModel>): void {
    ctx.setState(initialState);
  }

  private navigateToRulesList(hubId: number, compartmentId: number): void {
    this.navigateUserService.navigateUserToAbsolutePath(
      `/dashboard/administration/hubs/${hubId}/compartments/${compartmentId}/rules`,
    );
  }
}
